import React from "react";
import { useTranslation } from "react-i18next";

import { useAdminTransferContext } from "@context/AdminTransferContext";
import Heading1 from "@components/layout/Heading";
import ConfirmationDetail from "@components/shared/ConfirmationDetail";
import ConfirmModal from "@components/shared/ConfirmModal";
import { useCreateAdminTransfer } from "@hooks/mutation/useCreateAdminTransfer";
import { toastError, toastWarning } from "@utils/toast";
import { useEvidenceContext } from "@context/shared/EvidenceContext";
import { DBTables } from "@utils/constants/dbTables";
import { DraftNote } from "@components/notes/NoteComposer";
import { flushNoteStepDraft } from "@components/notes/flushNoteStepDraft";
import { mergeDraftNoteIntoRow } from "@components/notes/mergeDraftNoteIntoRow";

type AdminTransferLedgerProps = {
  /** Optional draft from the wizard's NoteStep; flushed after entity create succeeds. */
  draftNote?: DraftNote;
};

const AdminTransferLedger: React.FunctionComponent<AdminTransferLedgerProps> = ({
  draftNote,
}) => {
  const { t } = useTranslation();
  const {
    stepHelpers,
    details,
    setDetails,
    setWorkflowCompleted,
    setWorkflowInstance,
    navigateForCancel,
    confirmData,
  } = useAdminTransferContext();
  const { uploadEvidences, isSubmitting, getEvidencesInfo } =
    useEvidenceContext();
  const { mutateAsync: createAdminTransferMutation, isLoading } =
    useCreateAdminTransfer();
  const [showConfirmModal, setShowConfirmModal] = React.useState(false);

  const handleOnConfirm = async () => {
    try {
      const {
        extractionPoint: { id: extractionPointId = "" },
        extractionRight: { id: extractionRightId = "" },
        subscriber: { id: subscriberId = "" },
      } = details;

      const [res, workflowInstance] = await createAdminTransferMutation({
        subscriberId,
        extractionPointId,
        extractionRightId,
      });

      await uploadEvidences({
        description: t("approval.admin_transfer.create.step_5.description", {
          extractionRightName: details.extractionRight.name,
          extractionPointName: details.extractionPoint.name,
        }),
        references: [
          {
            referenceId: res.id,
            referenceTable: DBTables.AdministrativeApproval,
          },
          {
            referenceId: subscriberId,
            referenceTable: DBTables.Subscribers,
          },
          {
            referenceId: res.level1ResourceId,
            referenceTable: DBTables.WRSHierarchies,
          },
          {
            referenceId: workflowInstance?.id,
            referenceTable: DBTables.WorkflowInstances,
          },
        ],
      });

      // FR-1.1 + FR-1 invariant: flush optional NoteStep draft AFTER the entity
      // commits. Note failure does NOT roll back the entity; user gets a warning
      // toast and can re-add via the audit-trail panel.
      const noteResult = await flushNoteStepDraft(
        draftNote,
        DBTables.AdministrativeApproval,
        res.id,
      );
      if (noteResult.kind === "failed") {
        toastWarning(
          t("notes.step.flush_failed", {
            artifact: t("approval.admin_transfer.title"),
          }),
        );
      }

      setDetails({ ...details, id: res.id });
      setWorkflowInstance(workflowInstance);
      setWorkflowCompleted(true);
    } catch (error: any) {
      toastError(
        t("common.network_error", {
          error: error?.response?.data?.message || error?.message,
        })
      );
    }
    setShowConfirmModal(false);
  };

  return (
    <>
      <div className="flex flex-col gap-6 p-6 grow justify-between">
        <div className="space-y-4">
          <Heading1 light>
            {t("approval.admin_transfer.create.step_6.ledger_title")}
          </Heading1>

          <ConfirmationDetail
            data={[
              confirmData.level1Resource,
              confirmData.subscriber,
              confirmData.extractionRight,
              confirmData.extractionPoint,
              mergeDraftNoteIntoRow(getEvidencesInfo(), draftNote, t),
            ]}
            onEdit={stepHelpers.setStep}
          />
        </div>

        <footer className="flex gap-4 -mx-2 mt-2 p-6 pb-0 border-t border-gray-200">
          <button
            type="button"
            className="btn-outline-primary"
            onClick={stepHelpers.goToPrevStep}
          >
            {t("common.prev_step")}
          </button>

          <button
            type="button"
            className="btn-primary"
            onClick={() => setShowConfirmModal(true)}
            disabled={isSubmitting || isLoading}
          >
            {t("common.ledger")}
          </button>

          <button
            type="button"
            className="btn-outline-primary"
            onClick={navigateForCancel}
          >
            {t("common.cancel")}
          </button>
        </footer>
      </div>
      <ConfirmModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={() => handleOnConfirm()}
        isSubmitting={isLoading || isSubmitting}
        confirmText={t("common.ledger") as string}
      >
        {t("approval.admin_transfer.create.step_6.confirm_text")}
      </ConfirmModal>
    </>
  );
};

export default AdminTransferLedger;
